import { useEffect, useRef, useState } from 'react';
import {
  MdFormatAlignLeft,
  MdFormatAlignCenter,
  MdFormatAlignRight,
  MdFormatBold,
  MdFormatItalic,
  MdFormatStrikethrough,
  MdLooksOne,
  MdLooksTwo,
  MdLooks3,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatQuote,
  MdCode,
  MdLink,
} from 'react-icons/md';

import styles from './richtexteditormodal.module.css';

const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE']);

const escapeHtml = (str) =>
  String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const applyInlineMarkdown = (text) => {
  let html = escapeHtml(text || '');

  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/~~([^~]+)~~/g, '<s>$1</s>');
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  return html;
};

const getNormalizedAlign = (node) => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) return '';
  const attrAlign = (node.getAttribute('align') || '').toLowerCase();
  if (['left', 'center', 'right', 'justify'].includes(attrAlign)) return attrAlign;

  const styleAlign = (node.style?.textAlign || '').toLowerCase();
  if (['left', 'center', 'right', 'justify'].includes(styleAlign)) return styleAlign;

  return '';
};

const markdownToHtml = (markdown) => {
  const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
  const chunks = [];
  let i = 0;

  const closeList = (ctx) => {
    if (!ctx.type) return '';
    const result = `<${ctx.type}>${ctx.items.join('')}</${ctx.type}>`;
    ctx.type = null;
    ctx.items = [];
    return result;
  };

  const listCtx = { type: null, items: [] };

  while (i < lines.length) {
    const rawLine = lines[i];
    const line = rawLine.trimEnd();

    if (!line.trim()) {
      const closed = closeList(listCtx);
      if (closed) chunks.push(closed);
      i++;
      continue;
    }

    // Preserva blocos HTML salvos no markdown (ex.: alinhamento com align)
    if (/^\s*<[^>]+>/.test(line)) {
      const closed = closeList(listCtx);
      if (closed) chunks.push(closed);

      const htmlLines = [];
      while (i < lines.length) {
        const current = lines[i];
        if (!current.trim()) break;
        htmlLines.push(current);
        i++;
      }
      chunks.push(htmlLines.join('\n'));
      continue;
    }

    // Bloco de codigo com fences ```
    if (/^\s*```/.test(line)) {
      const closed = closeList(listCtx);
      if (closed) chunks.push(closed);

      i++;
      const codeLines = [];
      while (i < lines.length && !/^\s*```/.test(lines[i])) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length && /^\s*```/.test(lines[i])) i++;
      chunks.push(`<pre><code>${escapeHtml(codeLines.join('\n'))}</code></pre>`);
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const closed = closeList(listCtx);
      if (closed) chunks.push(closed);

      const level = headingMatch[1].length;
      const content = applyInlineMarkdown(headingMatch[2]);
      chunks.push(`<h${level}>${content}</h${level}>`);
      i++;
      continue;
    }

    const blockQuoteMatch = line.match(/^>\s?(.*)$/);
    if (blockQuoteMatch) {
      const closed = closeList(listCtx);
      if (closed) chunks.push(closed);

      const quoteLines = [];
      while (i < lines.length) {
        const candidate = lines[i].trimEnd();
        const m = candidate.match(/^>\s?(.*)$/);
        if (!m) break;
        quoteLines.push(m[1]);
        i++;
      }
      const text = quoteLines.map((l) => applyInlineMarkdown(l)).join('<br/>');
      chunks.push(`<blockquote>${text}</blockquote>`);
      continue;
    }

    const olMatch = line.match(/^\d+\.\s+(.+)$/);
    if (olMatch) {
      if (listCtx.type && listCtx.type !== 'ol') {
        chunks.push(closeList(listCtx));
      }
      listCtx.type = 'ol';
      listCtx.items.push(`<li>${applyInlineMarkdown(olMatch[1])}</li>`);
      i++;
      continue;
    }

    const ulMatch = line.match(/^[-*]\s+(.+)$/);
    if (ulMatch) {
      if (listCtx.type && listCtx.type !== 'ul') {
        chunks.push(closeList(listCtx));
      }
      listCtx.type = 'ul';
      listCtx.items.push(`<li>${applyInlineMarkdown(ulMatch[1])}</li>`);
      i++;
      continue;
    }

    const closed = closeList(listCtx);
    if (closed) chunks.push(closed);
    chunks.push(`<p>${applyInlineMarkdown(line)}</p>`);
    i++;
  }

  const closed = closeList(listCtx);
  if (closed) chunks.push(closed);

  return chunks.join('');
};

const inlineNodeToMarkdown = (node) => {
  if (!node) return '';

  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const tag = node.tagName.toUpperCase();
  const children = Array.from(node.childNodes).map(inlineNodeToMarkdown).join('');

  if (tag === 'STRONG' || tag === 'B') return `**${children}**`;
  if (tag === 'EM' || tag === 'I') return `*${children}*`;
  if (tag === 'S' || tag === 'DEL') return `~~${children}~~`;
  if (tag === 'CODE') return `\`${children}\``;
  if (tag === 'A') {
    const href = node.getAttribute('href') || '';
    return `[${children || href}](${href})`;
  }
  if (tag === 'BR') return '\n';

  return children;
};

const blockNodeToMarkdown = (node, olState) => {
  if (!node) return '';

  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent || '').trim();
    return text ? `${text}\n\n` : '';
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const tag = node.tagName.toUpperCase();
  const inline = () => Array.from(node.childNodes).map(inlineNodeToMarkdown).join('').trim();
  const align = getNormalizedAlign(node);
  const tagLower = tag.toLowerCase();

  if (tag === 'PRE') {
    const codeText = node.textContent || '';
    return `\`\`\`\n${codeText}\n\`\`\`\n\n`;
  }

  if (tag === 'H1' || tag === 'H2' || tag === 'H3' || tag === 'H4' || tag === 'H5' || tag === 'H6') {
    if (align && align !== 'left') {
      return `<${tagLower} align="${align}">${node.innerHTML}</${tagLower}>\n\n`;
    }
    const level = Number(tag.replace('H', '')) || 1;
    return `${'#'.repeat(level)} ${inline()}\n\n`;
  }

  if (tag === 'P' || tag === 'DIV') {
    if (align && align !== 'left') {
      return `<${tagLower} align="${align}">${node.innerHTML}</${tagLower}>\n\n`;
    }
    return `${inline()}\n\n`;
  }

  if (tag === 'BLOCKQUOTE') {
    if (align && align !== 'left') {
      return `<blockquote align="${align}">${node.innerHTML}</blockquote>\n\n`;
    }
    const lines = inline().split('\n').filter(Boolean);
    return `${lines.map((l) => `> ${l}`).join('\n')}\n\n`;
  }

  if (tag === 'UL') {
    const lines = Array.from(node.children)
      .filter((li) => li.tagName?.toUpperCase() === 'LI')
      .map((li) => `- ${Array.from(li.childNodes).map(inlineNodeToMarkdown).join('').trim()}`);
    return `${lines.join('\n')}\n\n`;
  }

  if (tag === 'OL') {
    let idx = 1;
    const lines = Array.from(node.children)
      .filter((li) => li.tagName?.toUpperCase() === 'LI')
      .map((li) => `${idx++}. ${Array.from(li.childNodes).map(inlineNodeToMarkdown).join('').trim()}`);
    return `${lines.join('\n')}\n\n`;
  }

  if (tag === 'LI') {
    if (olState?.isOrdered) {
      return `${olState.next()}. ${inline()}\n`;
    }
    return `- ${inline()}\n`;
  }

  return `${inline()}\n\n`;
};

const htmlToMarkdown = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html || ''}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';

  const nodes = Array.from(root.childNodes);
  let markdown = '';

  nodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && !BLOCK_TAGS.has(node.tagName.toUpperCase())) {
      markdown += `${inlineNodeToMarkdown(node)}\n\n`;
    } else {
      markdown += blockNodeToMarkdown(node);
    }
  });

  return markdown
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
};

function RichTextEditorModal({
  isOpen,
  title = 'Editor de texto',
  value = '',
  onSave,
  onClose,
  confirmLabel = 'Aplicar',
  helperText = '',
}) {
  const editorRef = useRef(null);
  const draftHtmlRef = useRef('');
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    strikeThrough: false,
    insertUnorderedList: false,
    insertOrderedList: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false,
    h1: false,
    h2: false,
    h3: false,
  });

  useEffect(() => {
    if (isOpen) {
      const initialHtml = markdownToHtml(value || '');
      draftHtmlRef.current = initialHtml;
      if (editorRef.current) {
        editorRef.current.innerHTML = initialHtml;
      }
    }
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const timer = setTimeout(() => {
      editorRef.current?.focus();
    }, 20);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onSelectionChange = () => refreshActiveFormats();
    document.addEventListener('selectionchange', onSelectionChange);

    const refreshTimer = setTimeout(() => {
      refreshActiveFormats();
    }, 30);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      clearTimeout(refreshTimer);
      document.removeEventListener('selectionchange', onSelectionChange);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  const syncDraft = () => {
    draftHtmlRef.current = editorRef.current?.innerHTML || '';
  };

  const refreshActiveFormats = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const anchorNode = sel.anchorNode;
    if (!anchorNode || !editor.contains(anchorNode)) return;

    const safeState = (cmd) => {
      try {
        return !!document.queryCommandState(cmd);
      } catch {
        return false;
      }
    };

    let formatBlock = '';
    try {
      formatBlock = String(document.queryCommandValue('formatBlock') || '').toLowerCase();
    } catch {
      formatBlock = '';
    }

    setActiveFormats({
      bold: safeState('bold'),
      italic: safeState('italic'),
      strikeThrough: safeState('strikeThrough'),
      insertUnorderedList: safeState('insertUnorderedList'),
      insertOrderedList: safeState('insertOrderedList'),
      justifyLeft: safeState('justifyLeft'),
      justifyCenter: safeState('justifyCenter'),
      justifyRight: safeState('justifyRight'),
      h1: /h1/.test(formatBlock),
      h2: /h2/.test(formatBlock),
      h3: /h3/.test(formatBlock),
    });
  };

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const runCommand = (command, valueArg = null) => {
    focusEditor();
    document.execCommand(command, false, valueArg);
    syncDraft();
    refreshActiveFormats();
  };

  const getCurrentFormatBlock = () => {
    try {
      const raw = String(document.queryCommandValue('formatBlock') || '').toLowerCase().trim();
      return raw.replace(/[<>]/g, '');
    } catch {
      return '';
    }
  };

  const toggleFormatBlock = (targetTag) => {
    focusEditor();
    const current = getCurrentFormatBlock();
    const normalizedTarget = String(targetTag || '').toLowerCase().trim();

    if (current === normalizedTarget) {
      document.execCommand('formatBlock', false, 'p');
    } else {
      document.execCommand('formatBlock', false, normalizedTarget);
    }

    syncDraft();
    refreshActiveFormats();
  };

  const applyHeading = (level) => {
    toggleFormatBlock(`h${level}`);
  };

  const applyQuote = () => {
    toggleFormatBlock('blockquote');
  };

  const applyCode = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    if (range.collapsed) return;

    const selectedText = sel.toString();
    if (selectedText.includes('\n')) {
      document.execCommand('insertHTML', false, `<pre><code>${escapeHtml(selectedText)}</code></pre>`);
    } else {
      document.execCommand('insertHTML', false, `<code>${escapeHtml(selectedText)}</code>`);
    }
    syncDraft();
    refreshActiveFormats();
  };

  const applyLink = () => {
    const url = window.prompt('Informe a URL do link', 'https://');
    if (!url) return;
    runCommand('createLink', url);
  };

  const handleEditorHotkeys = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return;

    const key = e.key.toLowerCase();
    if (key === 'b') {
      e.preventDefault();
      runCommand('bold');
    }
    if (key === 'i') {
      e.preventDefault();
      runCommand('italic');
    }
    if (key === 'k') {
      e.preventDefault();
      applyLink();
    }
  };

  const handleSave = () => {
    const markdown = htmlToMarkdown(draftHtmlRef.current);
    onSave?.(markdown);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            Fechar
          </button>
        </div>

        {helperText ? <p className={styles.helperText}>{helperText}</p> : null}

        <div className={styles.toolbar}>
          <button type="button" className={activeFormats.justifyLeft ? styles.activeToolButton : ''} title="Alinhar a esquerda" onClick={() => runCommand('justifyLeft')}><MdFormatAlignLeft /></button>
          <button type="button" className={activeFormats.justifyCenter ? styles.activeToolButton : ''} title="Centralizar" onClick={() => runCommand('justifyCenter')}><MdFormatAlignCenter /></button>
          <button type="button" className={activeFormats.justifyRight ? styles.activeToolButton : ''} title="Alinhar a direita" onClick={() => runCommand('justifyRight')}><MdFormatAlignRight /></button>
          <button type="button" className={activeFormats.bold ? styles.activeToolButton : ''} title="Negrito" onClick={() => runCommand('bold')}><MdFormatBold /></button>
          <button type="button" className={activeFormats.italic ? styles.activeToolButton : ''} title="Italico" onClick={() => runCommand('italic')}><MdFormatItalic /></button>
          <button type="button" className={activeFormats.strikeThrough ? styles.activeToolButton : ''} title="Tachado" onClick={() => runCommand('strikeThrough')}><MdFormatStrikethrough /></button>
          <button type="button" className={activeFormats.h1 ? styles.activeToolButton : ''} title="Titulo H1" onClick={() => applyHeading(1)}><MdLooksOne /></button>
          <button type="button" className={activeFormats.h2 ? styles.activeToolButton : ''} title="Titulo H2" onClick={() => applyHeading(2)}><MdLooksTwo /></button>
          <button type="button" className={activeFormats.h3 ? styles.activeToolButton : ''} title="Titulo H3" onClick={() => applyHeading(3)}><MdLooks3 /></button>
          <button type="button" className={activeFormats.insertUnorderedList ? styles.activeToolButton : ''} title="Lista" onClick={() => runCommand('insertUnorderedList')}><MdFormatListBulleted /></button>
          <button type="button" className={activeFormats.insertOrderedList ? styles.activeToolButton : ''} title="Lista numerada" onClick={() => runCommand('insertOrderedList')}><MdFormatListNumbered /></button>
          <button type="button" title="Citacao" onClick={applyQuote}><MdFormatQuote /></button>
          <button type="button" title="Codigo" onClick={applyCode}><MdCode /></button>
          <button type="button" title="Link" onClick={applyLink}><MdLink /></button>
        </div>

        <div className={styles.editorWrap}>
          <div
            ref={editorRef}
            className={styles.editor}
            dir="ltr"
            contentEditable
            suppressContentEditableWarning
            onInput={syncDraft}
            onBlur={() => {
              syncDraft();
              refreshActiveFormats();
            }}
            onKeyDown={handleEditorHotkeys}
            onKeyUp={refreshActiveFormats}
            onMouseUp={refreshActiveFormats}
          />
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className={styles.confirmBtn} onClick={handleSave}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RichTextEditorModal;
